import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Placement as BootstrapOverlayPlacement } from 'react-bootstrap/Overlay';
import { Markup } from 'interweave';

import * as K from 'common/constants';
import { DynamicTestId } from 'common/element';
import { truncateText } from 'common/util/string';

export enum TooltipPlacement {
  TOP = 'top',
  BOTTOM = 'bottom',
  LEFT = 'left',
  RIGHT = 'right',
}

type TruncateWithTooltipProps = {
  text: string;
  id: string;
  maxLength: number;
  placement?: TooltipPlacement;
  ellipsisPlacement?: K.PLACEMENT;
  noEllipsis?: boolean;
  testId?: DynamicTestId;
};

/**
 * This component truncates long text, adds an ellipsis, and provides a tooltip with the full text.
 * If the text is not too long, it is returned unchanged with no tooltip.
 *
 * @prop text                 text
 * @prop id                   unique ID for the tooltip
 * @prop maxLength            maximum length of the rendered text after adding ellipsi
 * @prop placement            where the tooltip is placed relative to the text (defaults to top)
 * @prop ellipsisPlacement    where excess text is removed and replaced by an ellipsis (defaults to end)
 * @prop noEllipsis           if true, omit the ellipsis
 * @prop testId               unique string for testing
 */
const TruncateWithTooltip = ({
  text,
  id,
  maxLength = 20,
  placement = TooltipPlacement.TOP,
  ellipsisPlacement = K.PLACEMENT.END,
  noEllipsis = false,
  testId,
}: TruncateWithTooltipProps): JSX.Element => {
  // We make sure it's a text, otherwise even length calculations are wrong
  const strText = String(text ?? '');

  if (strText.length <= maxLength) {
    return <span data-test-id={testId}>{strText}</span>;
  }

  const truncatedText = noEllipsis
    ? truncateText(strText, maxLength, ellipsisPlacement, '', 0)
    : truncateText(strText, maxLength, ellipsisPlacement, '<span>&hellip;</span>');

  // the snippet of text thats was removed from the original text and was replaced by the ellipsis
  const replacedText = (): string => {
    const [firstChunk, lastChunk] = truncatedText.split('<span>&hellip;</span>');
    return strText.replace(firstChunk, '').replace(lastChunk, '');
  };

  // whenever we copy the truncated text, we should include the text that was replaced if there is an ellipsis
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleCopy = async () => {
    const copiedText = await navigator.clipboard.readText();
    const replaced = replacedText();
    const pasteText = copiedText.replace('…', replaced);
    await navigator.clipboard.writeText(pasteText);
  };

  return (
    <OverlayTrigger
      key={strText}
      placement={placement as BootstrapOverlayPlacement}
      overlay={<Tooltip id={id}>{strText}</Tooltip>}
    >
      <span onCopy={handleCopy} data-test-id={testId}>
        <Markup noWrap content={truncatedText} />
      </span>
    </OverlayTrigger>
  );
};

export default TruncateWithTooltip;
